/**
 * Cached loader for the Market Place securities payload.
 *
 * Same module-level cache + in-flight dedupe idiom as portfolioOptions.js.
 * Cleared on logout via AuthContext.clearTokens so a new user never sees the
 * previous user's holdings (data-isolation requirement).
 */
import { API_ENDPOINTS } from '../../../config/api';
import { refreshIfStale } from '../../../utils/cacheFreshness';

let cache = null; // resolved /api/marketplace/securities payload
let cachedAt = 0; // when `cache` was last read from the backend
let inflight = null; // dedupes concurrent callers

function authHeader() {
  const token =
    localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// `force` bypasses the cache; `fetcher` (AuthContext's fetchWithAuth) adds 401 ->
// refresh -> retry, which the freshness watcher uses (see utils/cacheFreshness).
export function loadMarketplaceSecurities({ force = false, fetcher } = {}) {
  if (!force && cache) return Promise.resolve(cache);
  if (!force && inflight) return inflight;
  const doFetch = fetcher || ((url) => fetch(url, { headers: authHeader() }));
  inflight = doFetch(API_ENDPOINTS.marketplace.securities)
    .then((res) => {
      if (!res.ok) throw new Error(`marketplace ${res.status}`);
      return res.json();
    })
    .then((data) => {
      cache = data;
      cachedAt = Date.now();
      inflight = null;
      return data;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

/** The freshness refresher for this cache (see utils/cacheFreshness). */
export function refreshStaleMarketplaceSecurities(fetcher) {
  refreshIfStale({ loaded: cache, busy: inflight, readAt: cachedAt, reload: loadMarketplaceSecurities }, fetcher);
}

/** Drop the cached payload (call on logout / token clear). */
export function clearMarketplaceSecurities() {
  cache = null;
  inflight = null;
}
